import { Button, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useGetDeviceModels } from '../hooks/useDeviceModels'
import { useCloneDevice } from '../hooks/useDeviceMutations'
import {
  useGetExtensionsLine,
  useGetUsers,
} from '../hooks/useDeviceReferenceData'
import { useGetDevice } from '../hooks/useDevices'
import { useMacValidation } from '../hooks/useMacValidation'
import { useDevicesI18n } from '../i18n'
import type { ProvisioningDeviceListItem } from '../types/device.types'
import {
  buildClonePayload,
  type CloneFormState,
  getCloneMaxLineButtons,
} from '../utils/deviceClone'
import {
  buildExtensionByUserIdMap,
  filterUsersWithLineExtensions,
  getAssignedUserId,
  type LineExtensionOption,
} from '../utils/helpers'
import { mapSourceButtonsForClone } from './buttonDraftUtils'
import { getSuggestedStationLabel } from './DeviceForm'
import {
  ExtensionSelect,
  MacAddressInput,
  ModelSelect,
  StationLabelInput,
  UserSelect,
} from './DeviceFormFields'
import { SideSheetDialog } from './SideSheetDialog'

interface DeviceCloneDialogProps {
  isOpen: boolean
  onClose: () => void
  sourceDevice: ProvisioningDeviceListItem | null
  pbxId: number
  onSuccess: (newDeviceId: number) => void
  onError?: (error: unknown) => void
}

export const DeviceCloneDialog = React.memo<DeviceCloneDialogProps>(
  ({ isOpen, onClose, sourceDevice, pbxId, onSuccess, onError }) => {
    const { text } = useDevicesI18n()
    const { data: models = [] } = useGetDeviceModels()
    const { data: extensions = [] } = useGetExtensionsLine(pbxId)
    const { data: users = [] } = useGetUsers(pbxId)

    const { data: sourceDetail, isLoading: isLoadingDetail } = useGetDevice(
      pbxId,
      sourceDevice?.id ?? 0,
      { enabled: isOpen && !!sourceDevice?.id },
    )

    const cloneMutation = useCloneDevice(pbxId)

    const [formState, setFormState] = useState<CloneFormState>({
      device_model: 1,
      device_mac_address: '',
      station_label: '',
      assigned_user_id: '',
      primary_extension_id: '',
    })

    const typedExtensions = extensions as LineExtensionOption[]
    const hasAssignedUserMetadata = typedExtensions.some(
      (ext) => getAssignedUserId(ext) !== undefined,
    )
    const filteredPrimaryExtensions = useMemo(() => {
      if (!hasAssignedUserMetadata) {
        return typedExtensions
      }
      if (formState.assigned_user_id === '') {
        return typedExtensions
      }
      return typedExtensions.filter(
        (ext) => getAssignedUserId(ext) === formState.assigned_user_id,
      )
    }, [typedExtensions, hasAssignedUserMetadata, formState.assigned_user_id])

    const usersSortedForClone = useMemo(() => {
      const extensionByUserId = buildExtensionByUserIdMap(typedExtensions)
      return filterUsersWithLineExtensions(
        users,
        typedExtensions,
        extensionByUserId,
      ).sort((a, b) => {
        const aExt = extensionByUserId.get(a.id)
        const bExt = extensionByUserId.get(b.id)

        if (aExt != null && bExt != null && aExt !== bExt) {
          return aExt - bExt
        }

        return a.full_name.localeCompare(b.full_name)
      })
    }, [users, typedExtensions])

    const lastSuggestedStationLabelRef = useRef<string>('')

    const { macError, macValidating, setMacError, validateMac, clearMacError } =
      useMacValidation({
        pbxId,
        messages: {
          invalid: text('devices.form.macInvalid', 'MAC address is invalid'),
          alreadyInUse: text(
            'devices.form.macAlreadyInUse',
            'MAC address already in use',
          ),
        },
      })

    const resetForm = useCallback(() => {
      if (sourceDevice) {
        const modelExists = models.some(
          (m) => m.id === sourceDevice.device_model,
        )
        const device_model = modelExists
          ? sourceDevice.device_model
          : (models[0]?.id ?? sourceDevice.device_model)
        lastSuggestedStationLabelRef.current = ''
        setFormState({
          device_model,
          device_mac_address: '',
          station_label: '',
          assigned_user_id: sourceDevice.assigned_user?.id ?? '',
          primary_extension_id: sourceDevice.primary_extension?.id ?? '',
        })
        clearMacError()
      }
    }, [sourceDevice, models, clearMacError])

    const wasOpenRef = useRef(false)

    useEffect(() => {
      const justOpened = isOpen && !wasOpenRef.current
      wasOpenRef.current = isOpen
      if (justOpened && sourceDevice) {
        resetForm()
      }
    }, [isOpen, sourceDevice, resetForm])

    useEffect(() => {
      if (formState.assigned_user_id === '') return
      if (!hasAssignedUserMetadata) return

      if (filteredPrimaryExtensions.length === 0) {
        if (formState.primary_extension_id === '') return
        setFormState((prev) => ({ ...prev, primary_extension_id: '' }))
        return
      }

      const currentExtensionIsValid = filteredPrimaryExtensions.some(
        (ext) => ext.id === formState.primary_extension_id,
      )
      if (currentExtensionIsValid) return

      setFormState((prev) => ({
        ...prev,
        primary_extension_id: filteredPrimaryExtensions[0].id,
      }))
    }, [
      formState.assigned_user_id,
      formState.primary_extension_id,
      filteredPrimaryExtensions,
      hasAssignedUserMetadata,
    ])

    useEffect(() => {
      const suggested = getSuggestedStationLabel(
        formState.assigned_user_id,
        formState.primary_extension_id,
        users,
        filteredPrimaryExtensions,
      )
      const prevSuggested = lastSuggestedStationLabelRef.current
      lastSuggestedStationLabelRef.current = suggested
      setFormState((prev) => {
        const shouldAutoUpdate =
          prev.station_label === '' || prev.station_label === prevSuggested
        if (shouldAutoUpdate && prev.station_label !== suggested) {
          return { ...prev, station_label: suggested }
        }
        return prev
      })
    }, [
      formState.assigned_user_id,
      formState.primary_extension_id,
      users,
      filteredPrimaryExtensions,
    ])

    const updateField = useCallback(
      <K extends keyof CloneFormState>(field: K, value: CloneFormState[K]) => {
        setFormState((prev) => ({ ...prev, [field]: value }))
        if (field === 'device_mac_address') {
          clearMacError()
        }
      },
      [clearMacError],
    )

    const handleMacBlur = useCallback(() => {
      void validateMac(formState.device_mac_address)
    }, [validateMac, formState.device_mac_address])

    const handleSubmit = useCallback(() => {
      void (async () => {
        const mac = formState.device_mac_address.trim()
        if (!mac) {
          setMacError(
            text('devices.form.macRequired', 'MAC address is required'),
          )
          return
        }

        const macValid = await validateMac(mac)
        if (!macValid) return

        if (!formState.station_label.trim()) {
          return
        }
        if (formState.primary_extension_id === '') {
          return
        }
        if (!sourceDevice) return

        const selectedModel = models.find(
          (m) => m.id === formState.device_model,
        )
        const modelSupportsSidecar = selectedModel?.sidecar_supported ?? false

        const validExtensionIds = new Set(extensions.map((e) => e.id))
        const sourceButtons = mapSourceButtonsForClone(
          sourceDetail?.buttons ?? [],
          validExtensionIds,
        )

        const payload = buildClonePayload(
          formState,
          sourceDetail ?? null,
          selectedModel,
        )
        const maxLineButtons = getCloneMaxLineButtons(payload, selectedModel)
        if (
          payload.primary_extension_id == null ||
          !validExtensionIds.has(payload.primary_extension_id)
        ) {
          return
        }

        cloneMutation.mutate(
          {
            sourceDeviceId: sourceDevice.id,
            payload,
            sourceButtons,
            modelSupportsSidecar,
            maxLineButtons,
          },
          {
            onSuccess: (data) => {
              onClose()
              onSuccess(data.id)
            },
            onError: (err) => {
              onError?.(err)
            },
          },
        )
      })()
    }, [
      formState,
      sourceDevice,
      sourceDetail,
      models,
      extensions,
      validateMac,
      setMacError,
      cloneMutation,
      onClose,
      onSuccess,
      onError,
    ])

    const hasValidModel = models.some((m) => m.id === formState.device_model)
    const primaryExtensionError =
      formState.assigned_user_id !== '' &&
      hasAssignedUserMetadata &&
      filteredPrimaryExtensions.length === 0
        ? text(
            'devices.form.noExtensionsForUser',
            'The assigned user does not have any valid line extensions. Select a different user or assign a line extension before saving.',
          )
        : undefined
    const canSubmit =
      hasValidModel &&
      formState.device_mac_address.trim().length > 0 &&
      formState.station_label.trim().length > 0 &&
      formState.assigned_user_id !== '' &&
      formState.primary_extension_id !== '' &&
      !macError &&
      !cloneMutation.isPending &&
      !isLoadingDetail

    if (!sourceDevice) return null

    return (
      <SideSheetDialog
        isOpen={isOpen}
        onClose={onClose}
        title={text('devices.clone.title', 'Clone device')}
        width="480px"
        body={
          <>
            <Text mb={4} color="fg.muted" fontSize="sm">
              {text(
                'devices.clone.description',
                'Create a new device from "{{sourceLabel}}". Enter a unique MAC address. All settings and buttons will be copied to the new device.',
                { sourceLabel: sourceDevice.station_label },
              )}
            </Text>
            {isLoadingDetail ? (
              <VStack py={8}>
                <Spinner size="lg" />
                <Text color="fg.muted">
                  {text(
                    'devices.clone.loadingSettings',
                    'Loading device settings…',
                  )}
                </Text>
              </VStack>
            ) : (
              <VStack gap={4} align="stretch">
                <ModelSelect
                  value={formState.device_model}
                  onChange={(value) => {
                    if (value !== '') updateField('device_model', value)
                  }}
                  models={models}
                  required
                />

                <MacAddressInput
                  value={formState.device_mac_address}
                  onChange={(value) => updateField('device_mac_address', value)}
                  onBlur={handleMacBlur}
                  onEnterKey={handleSubmit}
                  error={macError}
                  validating={macValidating}
                  required
                />

                <UserSelect
                  value={formState.assigned_user_id}
                  onChange={(value) => updateField('assigned_user_id', value)}
                  users={usersSortedForClone}
                  required
                />

                <ExtensionSelect
                  value={formState.primary_extension_id}
                  onChange={(value) =>
                    updateField('primary_extension_id', value)
                  }
                  extensions={filteredPrimaryExtensions}
                  error={primaryExtensionError}
                  required
                />

                <StationLabelInput
                  value={formState.station_label}
                  onChange={(value) => updateField('station_label', value)}
                  placeholder={text(
                    'devices.clone.stationLabelPlaceholder',
                    'Clone of device',
                  )}
                  required
                />
              </VStack>
            )}
          </>
        }
        footer={
          <HStack gap={3} justifyContent="flex-end">
            <Button
              type="button"
              variant="outline"
              colorPalette="neutral"
              onClick={onClose}
              disabled={cloneMutation.isPending}
            >
              {text('devices.common.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              colorPalette="brand"
              onClick={handleSubmit}
              loading={cloneMutation.isPending}
              disabled={!canSubmit}
            >
              {text('devices.clone.cloneButton', 'Clone device')}
            </Button>
          </HStack>
        }
      />
    )
  },
)

DeviceCloneDialog.displayName = 'DeviceCloneDialog'
